/**
 * CharacterSheet component - displays a character as a fantasy paper character sheet.
 * Styled like a handwritten sheet of paper with aged parchment aesthetic.
 */

import { useState } from 'react';
import { Link } from '@inertiajs/react';
import type { Character, StatWithLabel, SkillWithLabel } from '@/types/characters';
import { STAT_KEYS, SKILL_KEYS } from '@/types/characters';
import { HealthBar } from './HealthBar';

interface CharacterSheetProps {
    /** The character to display */
    character: Character;
    /** Whether to link to the character detail page (default: true) */
    linkToDetail?: boolean;
    /** Whether the current user is a GM */
    isGm?: boolean;
    /** Callback when the character is updated (e.g. after GM smite) */
    onCharacterUpdated?: () => void;
}

/**
 * Moth stamp SVG for Celestial Moth characters.
 */
function MothStamp() {
    return (
        <div className="absolute -top-2 -right-2 rotate-12 pointer-events-none">
            <div
                className="relative"
                style={{
                    width: '70px',
                    height: '70px',
                }}
            >
                {/* Stamp background - circular seal */}
                <div
                    className="absolute inset-0 rounded-full"
                    style={{
                        background: 'radial-gradient(circle, rgba(128, 90, 213, 0.25) 0%, rgba(128, 90, 213, 0.15) 60%, transparent 70%)',
                        border: '2px dashed rgba(128, 90, 213, 0.4)',
                    }}
                />
                {/* Moth silhouette */}
                <svg
                    viewBox="0 0 100 100"
                    className="absolute inset-0 w-full h-full"
                    style={{ filter: 'drop-shadow(0 0 2px rgba(128, 90, 213, 0.5))' }}
                >
                    {/* Moth body */}
                    <ellipse cx="50" cy="55" rx="6" ry="18" fill="rgba(128, 90, 213, 0.6)" />
                    {/* Moth head */}
                    <circle cx="50" cy="32" r="7" fill="rgba(128, 90, 213, 0.6)" />
                    {/* Antennae */}
                    <path
                        d="M45 28 Q40 18 35 15 M55 28 Q60 18 65 15"
                        stroke="rgba(128, 90, 213, 0.6)"
                        strokeWidth="2"
                        fill="none"
                        strokeLinecap="round"
                    />
                    {/* Upper wings */}
                    <path
                        d="M44 45 Q20 25 15 45 Q25 60 44 55"
                        fill="rgba(128, 90, 213, 0.5)"
                    />
                    <path
                        d="M56 45 Q80 25 85 45 Q75 60 56 55"
                        fill="rgba(128, 90, 213, 0.5)"
                    />
                    {/* Lower wings */}
                    <path
                        d="M44 58 Q25 65 25 78 Q40 75 46 65"
                        fill="rgba(128, 90, 213, 0.4)"
                    />
                    <path
                        d="M56 58 Q75 65 75 78 Q60 75 54 65"
                        fill="rgba(128, 90, 213, 0.4)"
                    />
                    {/* Wing patterns - eyes */}
                    <circle cx="30" cy="45" r="5" fill="rgba(180, 140, 255, 0.4)" />
                    <circle cx="70" cy="45" r="5" fill="rgba(180, 140, 255, 0.4)" />
                </svg>
                {/* "CELESTIAL" text curved around bottom */}
                <div
                    className="absolute bottom-1 left-1/2 -translate-x-1/2 text-purple-600/50 text-[6px] tracking-widest font-serif"
                    style={{ fontVariant: 'small-caps' }}
                >
                    CELESTIAL
                </div>
            </div>
        </div>
    );
}

/**
 * Get color for stat value - darker, more readable colors.
 */
function getStatColor(value: number): string {
    if (value >= 40) return 'text-emerald-700';
    if (value >= 25) return 'text-blue-800';
    if (value >= 15) return 'text-amber-800';
    return 'text-stone-500';
}

/**
 * Stat labels for character sheet.
 */
const STAT_DISPLAY: Record<string, string> = {
    head: 'Head',
    eyes: 'Eyes',
    mouth: 'Mouth',
    arms: 'Arms',
    body: 'Body',
    legs: 'Legs',
};

/**
 * Skill labels for character sheet.
 */
const SKILL_DISPLAY: Record<string, string> = {
    combat: 'Combat',
    magic: 'Magic',
    crafting: 'Crafting',
    healing: 'Healing',
    persuasion: 'Persuasion',
    stealth: 'Stealth',
    observation: 'Observation',
    tea: 'Tea',
    building: 'Building',
    herbalism: 'Herbalism',
    medicine: 'Medicine',
    cooking: 'Cooking',
};

/**
 * Small polaroid portrait component for character cards.
 */
function SmallPolaroid({ portraitUrl, characterName, rotation = 3 }: { portraitUrl: string | null; characterName: string; rotation?: number }) {
    return (
        <div
            className="flex-shrink-0 relative"
            style={{
                transform: `rotate(${rotation}deg)`,
            }}
        >
            {/* Polaroid frame */}
            <div
                className="relative"
                style={{
                    background: 'linear-gradient(145deg, #fefefe 0%, #f5f5f0 50%, #ebebdf 100%)',
                    padding: '6px 6px 22px 6px',
                    boxShadow: '2px 3px 8px rgba(0,0,0,0.2), 0 0 1px rgba(0,0,0,0.1)',
                    borderRadius: '1px',
                }}
            >
                {/* Photo area */}
                <div
                    className="relative overflow-hidden"
                    style={{
                        width: '112px',
                        height: '136px',
                        background: '#1a1a1a',
                    }}
                >
                    {portraitUrl ? (
                        <img
                            src={portraitUrl}
                            alt={`Portrait of ${characterName}`}
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-600">
                            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                        </div>
                    )}
                </div>
            </div>

            {/* Single tape piece */}
            <div
                className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-12 h-4 pointer-events-none"
                style={{
                    background: 'linear-gradient(180deg, rgba(210,190,140,0.85) 0%, rgba(195,175,125,0.8) 100%)',
                    transform: 'rotate(-5deg)',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
                    borderRadius: '1px',
                }}
            />
        </div>
    );
}

/**
 * Displays a character as a fantasy paper character sheet.
 */
const SMITE_MESSAGES = [
    "You bonk {name} on the head. For science.",
    "{name} stubs their toe on a conveniently placed rock.",
    "A mysterious piano falls from the sky onto {name}.",
    "{name} walks into a door. You definitely didn't push them.",
    "Lightning strikes {name}. What are the odds?",
    "{name} trips over absolutely nothing. Totally natural.",
    "A rogue sheep headbutts {name} in the kneecap.",
    "{name} receives a strongly-worded letter. It does {damage} damage.",
];

export function CharacterSheet({ character, linkToDetail = true, isGm = false, onCharacterUpdated }: CharacterSheetProps) {
    const stats = character.stats;
    const skills = character.skills;
    const [smiteAmount, setSmiteAmount] = useState(10);
    const [isSmiting, setIsSmiting] = useState(false);
    const [smiteMessage, setSmiteMessage] = useState<string | null>(null);

    const handleSmite = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsSmiting(true);
        setSmiteMessage(null);
        try {
            const response = await fetch(`/character/${character.id}/gm-smite`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '',
                },
                body: JSON.stringify({ damage: smiteAmount }),
            });
            const data = await response.json();
            if (data.success) {
                const template = SMITE_MESSAGES[Math.floor(Math.random() * SMITE_MESSAGES.length)];
                setSmiteMessage(template.replace('{name}', character.name).replace('{damage}', String(smiteAmount)));
                onCharacterUpdated?.();
            }
        } catch {
            setSmiteMessage("The smite hammer jammed. Try again.");
        } finally {
            setIsSmiting(false);
        }
    };

    const sheetContent = (
        <div
            className="relative p-5 rounded-sm overflow-hidden transition-transform hover:scale-[1.01]"
            style={{
                background: 'linear-gradient(135deg, #f5edd6 0%, #e8dcc4 50%, #f0e6d0 100%)',
                boxShadow: '4px 4px 12px rgba(0,0,0,0.3), -1px -1px 4px rgba(0,0,0,0.1)',
                border: '1px solid #c9b896',
            }}
        >
            {/* Aged edge effect at top */}
            <div
                className="absolute top-0 left-0 right-0 h-2 pointer-events-none"
                style={{
                    background: 'linear-gradient(180deg, rgba(139,115,85,0.15) 0%, transparent 100%)',
                }}
            />

            {/* Moth stamp for Celestial Moth */}
            {character.is_primary && <MothStamp />}

            {/* Header section with polaroid */}
            <div className="relative mb-4 pb-3 border-b-2 border-amber-900/20">
                <div className="flex items-start gap-3">
                    {/* Polaroid portrait */}
                    <SmallPolaroid
                        portraitUrl={character.portrait_url}
                        characterName={character.name}
                        rotation={-3}
                    />

                    {/* Character info */}
                    <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                            {/* Character name */}
                            <h3 className="text-2xl font-serif font-bold text-amber-900 truncate leading-tight">
                                {character.name}
                            </h3>
                            {/* Class and level */}
                            <div className="flex items-center gap-2 mt-1">
                                <span className="text-base font-serif text-amber-800">
                                    {character.class.label}
                                </span>
                                <span className="text-amber-700/50 font-bold">·</span>
                                <span className="text-base font-serif text-amber-800">
                                    Level {character.level ?? 0}
                                </span>
                            </div>
                            {/* Assigned Building - if character is assigned to a building */}
                            {character.assignedBuilding && (
                                <div className="flex items-center gap-1 mt-1">
                                    <span className="text-sm font-serif text-amber-700/70 italic">
                                        Assigned to: {character.assignedBuilding.name || character.assignedBuilding.building_type.name}
                                    </span>
                                </div>
                            )}
                        </div>
                        {/* XP indicator */}
                        <div className="text-right flex-shrink-0">
                            <div className="text-xs font-serif text-amber-700/70 uppercase tracking-wide">
                                XP
                            </div>
                            <div className="text-lg font-serif font-bold text-amber-900">
                                {character.xp.toLocaleString()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats section */}
            <div className="relative mb-4">
                <div className="text-xs font-serif text-amber-700/80 uppercase tracking-wider mb-2 font-semibold">
                    Attributes
                </div>
                <div className="grid grid-cols-3 gap-x-4 gap-y-2">
                    {STAT_KEYS.map((key) => {
                        const stat = stats[key] as StatWithLabel | undefined;
                        const value = stat?.value ?? 0;
                        const equipment = stat?.equipment ?? 0;
                        const label = STAT_DISPLAY[key];
                        return (
                            <div key={key} className="flex items-baseline gap-1.5">
                                <span className={`text-xl font-serif font-bold ${getStatColor(value)}`}>
                                    {value}
                                </span>
                                {equipment > 0 && (
                                    <span className="text-sm font-serif font-semibold text-emerald-600">
                                        +{equipment}
                                    </span>
                                )}
                                <span className="text-sm font-serif text-amber-800/70">
                                    {label}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Skills section */}
            <div className="relative">
                <div className="text-xs font-serif text-amber-700/80 uppercase tracking-wider mb-2 font-semibold">
                    Skills
                </div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                    {SKILL_KEYS.map((key) => {
                        const skill = skills[key] as SkillWithLabel | undefined;
                        const value = skill?.value ?? 0;
                        const equipment = skill?.equipment ?? 0;
                        const label = SKILL_DISPLAY[key];
                        return (
                            <div key={key} className="flex items-baseline gap-1.5">
                                <span className={`text-lg font-serif font-bold ${getStatColor(value)}`}>
                                    {value}
                                </span>
                                {equipment > 0 && (
                                    <span className="text-xs font-serif font-semibold text-emerald-600">
                                        +{equipment}
                                    </span>
                                )}
                                <span className="text-sm font-serif text-amber-800/70">
                                    {label}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Health and Inventory at bottom */}
            <div className="relative mt-4 pt-3 border-t-2 border-amber-900/20">
                <div className="flex items-center justify-between gap-4">
                    {/* Health bar */}
                    <div className="flex-1">
                        <div className="text-xs font-serif text-amber-700/80 uppercase tracking-wider mb-1 font-semibold">
                            Health
                        </div>
                        <HealthBar health={character.health} size="small" />
                    </div>
                    {/* Inventory slots */}
                    <div className="text-right">
                        <div className="text-xs font-serif text-amber-700/80 uppercase tracking-wider mb-1 font-semibold">
                            Inventory
                        </div>
                        <div className="flex items-baseline gap-1 justify-end">
                            <span className="text-lg font-serif font-bold text-amber-900">
                                {character.inventory_slots.used}/{character.inventory_slots.total}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Click indicator for interactivity */}
            {linkToDetail && (
                <div className="absolute bottom-2 right-2 text-xs font-serif text-amber-700/40 italic">
                    click to view
                </div>
            )}
        </div>
    );

    return (
        <div>
            {linkToDetail ? (
                <Link
                    href={`/character/${character.id}`}
                    className="block cursor-pointer"
                >
                    {sheetContent}
                </Link>
            ) : (
                sheetContent
            )}

            {/* GM Smite Panel - outside the link so clicks work */}
            {isGm && (
                <div
                    className="relative mt-1 p-3 rounded-sm overflow-hidden"
                    style={{
                        background: 'linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 50%, #2a2a2a 100%)',
                        border: '1px solid #444',
                        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.5)',
                    }}
                >
                    <div className="flex items-center gap-2 mb-2">
                        <span className="text-red-400 text-sm">&#9876;</span>
                        <span className="text-xs font-mono uppercase tracking-wider text-red-400/80">
                            GM Smite Hammer
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="number"
                            min={1}
                            max={9999}
                            value={smiteAmount}
                            onChange={(e) => setSmiteAmount(Math.max(1, parseInt(e.target.value) || 1))}
                            onClick={(e) => e.stopPropagation()}
                            className="w-20 bg-gray-800 border border-gray-600 rounded px-2 py-1 text-sm text-red-300 font-mono caret-gray-400 focus:outline-none focus:border-red-500"
                        />
                        <span className="text-xs text-gray-500 font-mono">dmg</span>
                        <button
                            onClick={handleSmite}
                            disabled={isSmiting}
                            className="px-3 py-1 bg-red-900/80 hover:bg-red-800 disabled:opacity-50 text-red-200 rounded text-xs font-mono uppercase tracking-wider transition-colors cursor-pointer"
                        >
                            {isSmiting ? 'Smiting...' : 'Smite'}
                        </button>
                    </div>
                    {smiteMessage && (
                        <div className="mt-2 text-xs font-serif italic text-amber-400/90">
                            {smiteMessage}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default CharacterSheet;
